import React from 'react';

import { isEmpty } from 'lodash';

import { useActionsContext } from '~/components/Claim/Actions/ActionsContext';
import { ACTION_PROCESSORS_MAP, defaultActionProcessor, getActionIcon } from '~/components/Claim/Actions/ActionsHelper';
import { ActionListResponse } from '~/components/Claim/Actions/types';
import { useClaimContext } from '~/components/Claim/ClaimContext';
import Button from '~/core/atoms/Buttons/Button';
import { MarkdownSection } from '~/core/atoms/MarkdownSection';
import OkIcon from '~/core/iconComponents/icons/OkIcon';
import ErrorDialog from '~/core/molecules/Dialog/ErrorDialog';
import SidePanel from '~/core/molecules/SidePanel';
import { post } from '~/utils/request';

interface ActionSidePanelProps {
  actionId: string;
  onClose: () => void;
  className?: string;
}

export const ActionSidePanel: React.FC<ActionSidePanelProps> = ({ actionId, onClose, className }) => {
  const [isActionsLoading, setIsActionsLoading] = React.useState(false);
  const [isDisplayActionError, setIsDisplayActionError] = React.useState(false);
  const { claimNumber } = useClaimContext();
  const { fetchActions, actions } = useActionsContext();

  const action = actions.find(({ id }) => id === actionId);

  if (!action) {
    console.error(`Action with ID ${actionId} not found`);
    return null;
  }

  const IconComponent = getActionIcon(action);
  const processedContent = ACTION_PROCESSORS_MAP[action.type]?.(action) || defaultActionProcessor(action);

  const handleExecuteAction = async () => {
    if (isEmpty(claimNumber)) {
      throw Error('Claim ID is required to execute the action');
    }
    try {
      setIsActionsLoading(true);
      await post<ActionListResponse>({
        url: `/api/v1/actions/${claimNumber}/${action.id}/execute`,
      });
      await fetchActions(claimNumber || '');
    } catch (error) {
      console.error('Failed to execute action:', error);
      setIsDisplayActionError(true);
    }
    setIsActionsLoading(false);
  };

  const executeText = action.metadata?.execute_text;

  return (
    <>
      <SidePanel
        icon={
          <div className="relative rounded-lg bg-blue-100 p-8">
            {action.is_done ? <OkIcon size={30} className="absolute top-[-14px] right-[-18px] text-green-600" /> : null}
            <IconComponent size={16} className="shrink-0 text-blue-600" />
          </div>
        }
        title={action.title}
        content={<MarkdownSection markdown={processedContent} />}
        footer={
          <Button
            onClick={handleExecuteAction}
            disabled={action.is_done || action.is_dismissed || isActionsLoading}
            size="sm"
            className="border-2 border-pink-600 bg-pink-100 text-pink-600 transition-colors duration-200 hover:bg-pink-200"
          >
            {typeof executeText === 'string' && executeText.trim() !== '' ? executeText : 'Mark as Complete'}
          </Button>
        }
        onClose={onClose}
        className={className}
      />
      <ErrorDialog
        isOpen={isDisplayActionError}
        onClose={() => setIsDisplayActionError(false)}
        title="Action Error"
        message="Failed to execute action. Please contact support if the issue persists."
      />
    </>
  );
};

export default ActionSidePanel;
